import React, { useState, useEffect } from 'react';
import { useApp } from '../App';
import { evaluateDay, parseFOHMeta } from '../api/rules';
import { getViewRangeDates, VIEW_RANGES, SESSION_STATUS, SHOW_CAREGIVER_SESSION_HINTS } from '../config/appConfig';
import { fetchEvents as gcalFetchEvents, patchEvent as gcalPatchEvent, deleteEvent as gcalDeleteEvent, createEvent, GCalAuthError } from '../lib/gcal';
import { fsLogChange, fsSet, fsDelete, fsGetCol, reconnectGCal } from '../lib/firebase';
import { pickupsForDate, overrideKey } from '../lib/pickups';
import { eventDates, formatEventTime } from '../lib/datetime';
import { isAssignedTo, stripAssigneeTag, buildFOHDescription, setEventAssignee } from '../api/assignments';
import { scheduleBatchedPublish } from '../api/schedulePublish';
import GCalReconnectPrompt from '../components/ui/GCalReconnectPrompt';
import DayCard, { PERSON_COLORS } from './DayCard';
import RecurringDefaults from './RecurringDefaults';
import ScheduleSitter from './ScheduleSitter';
import { ReminderIcon, RecurringIcon } from '../components/ui/icons';
import AccountMenu from '../components/ui/AccountMenu';

// ─── LOGISTICS MODULE ─────────────────────────────────────────────────────────

export default function LogisticsModule({ onBack, onSettings, initialView = 'nav', initialReviewFilter = null, initialWeek = 'this' }) {
  const [view, setView] = useState(initialView); // 'nav' | 'review' | 'schedule' | 'schedule_sitter' | 'schedule_travel' | 'schedule_event'
  const [eventType, setEventType] = useState(null); // 'general' | 'medical' | 'vacation' | 'other'

  // When entered directly from Home (initialView !== 'nav'), back exits to Home.
  // When navigated internally from the 2-tile nav, back returns to nav.
  const backToRoot = () => initialView === 'nav' ? setView('nav') : onBack();

  function selectScheduleOption(id) {
    if (id.startsWith('event_')) {
      setEventType(id.replace('event_', ''));
      setView('schedule_event');
    } else {
      setView(id);
    }
  }

  // The label has to match where backToRoot above actually sends you: the
  // Logistics nav tiles when entered internally, or straight out to Home
  // when this screen was deep-linked to directly (e.g. from a Home week card).
  const reviewBackLabel = initialView === 'nav' ? 'Logistics' : 'Home';

  if (view === 'review')          return <LookAheadReview onBack={backToRoot} backLabel={reviewBackLabel} onSettings={onSettings} initialFilter={initialReviewFilter} initialWeek={initialWeek} />;
  if (view === 'schedule')        return <ScheduleNav onSelect={selectScheduleOption} onBack={backToRoot} />;
  if (view === 'schedule_sitter') return <ScheduleSitter onBack={() => setView('schedule')} />;
  if (view === 'schedule_travel') return <ScheduleWorkTravel onBack={() => setView('schedule')} />;
  if (view === 'schedule_event')  return (
    <EventForm
      type={eventType}
      typeLabel={({ general: 'General', medical: 'Medical', vacation: 'Vacation' })[eventType] || 'Event'}
      onBack={() => setView('schedule')}
      onClose={() => setView('schedule')}
    />
  );

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Home</button>
        <h1>Family Logistics</h1>
        <AccountMenu onSettings={onSettings} />
      </div>
      <div className="logistics-tiles">
        <button className="logistics-tile" onClick={() => setView('review')}>
          <span className="logistics-tile__emoji">🌤️</span>
          <div>
            <div className="logistics-tile__label">Review & Assign</div>
            <div className="logistics-tile__desc">Review upcoming days, assign coverage, lock in</div>
          </div>
          <span className="logistics-tile__arrow">›</span>
        </button>
        <button className="logistics-tile" onClick={() => setView('schedule')}>
          <span className="logistics-tile__emoji">📅</span>
          <div>
            <div className="logistics-tile__label">Schedule</div>
            <div className="logistics-tile__desc">Book caregivers, log travel, create events</div>
          </div>
          <span className="logistics-tile__arrow">›</span>
        </button>
      </div>
    </div>
  );
}

// ─── SCHEDULE NAV ─────────────────────────────────────────────────────────────

function ScheduleNav({ onSelect, onBack }) {
  const options = [
    { id: 'schedule_sitter', emoji: '🌱', label: 'Caregiver',   desc: 'Book a caregiver and add to calendar' },
    { id: 'schedule_travel', emoji: '💼', label: 'Work Travel', desc: 'Log travel and notify your partner' },
    { id: 'event_general',   emoji: '🍃', label: 'General',     desc: 'One-off family event' },
    { id: 'event_medical',   emoji: '🌿', label: 'Medical',     desc: 'Doctor, dentist, therapy, etc.' },
    { id: 'event_vacation',  emoji: '🌊', label: 'Vacation',    desc: 'Family trip or travel' },
  ];
  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Logistics</button>
        <h1>Schedule</h1>
      </div>
      <div className="nav-list">
        {options.map(item => (
          <button key={item.id} className="nav-list-item" onClick={() => onSelect(item.id)}>
            <span className="nav-list-item__emoji">{item.emoji}</span>
            <div>
              <div className="nav-list-item__label">{item.label}</div>
              <div className="nav-list-item__desc">{item.desc}</div>
            </div>
            <span className="nav-list-item__arrow">›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── LOOK AHEAD REVIEW ────────────────────────────────────────────────────────

function LookAheadReview({ onBack, backLabel = 'Home', onSettings, initialFilter = null, initialWeek = 'this' }) {
  // 'next' has no standalone range option — VIEW_RANGES are cumulative from today,
  // so the smallest range that actually includes next week is 'plus_1_week'.
  const [rangeKey, setRangeKey] = useState(initialWeek === 'next' ? 'plus_1_week' : 'this_week');
  const [days, setDays] = useState([]);
  const [addEventDate, setAddEventDate] = useState(null); // isoDate when the "add event" flow is open
  const [addEventType, setAddEventType] = useState(null); // chosen ScheduleNav option id, or null = show the chooser
  const [showDefaults, setShowDefaults] = useState(false);
  const [pendingAssignments, setPendingAssignments] = useState({}); // { eventId: { assignee, action } }
  const [loading, setLoading] = useState(true);
  const [dataAsOf, setDataAsOf] = useState(null);
  const [stale, setStale] = useState(false);
  // viewFilter drives the top pill row: 'all' | 'needs_action' | 'mine'.
  // Defaults to 'needs_action' on every visit — already-covered days collapse
  // out of view so you're not scrolling past days with nothing to do. 'All'
  // is one tap away via the pill; 'mine' is still reachable the same way.
  const [viewFilter, setViewFilter] = useState('needs_action');
  const [showViewOptions, setShowViewOptions] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [error, setError] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [lockingIn, setLockingIn] = useState(false);
  const [lockInResult, setLockInResult] = useState(null);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  // personFilter: tray-only dropdown — 'all' or a specific care partner identifier
  const [personFilter, setPersonFilter] = useState('all');

  function handleToggleDay(isoDate) {
    setExpandedDay(prev => prev === isoDate ? null : isoDate);
  }

  const { familyData, currentUser } = useApp() || {};

  // Who am I? — enables the "Mine" chip
  const me = (familyData?.carePartners || []).find(
    cp => cp.email?.toLowerCase() === currentUser?.email?.toLowerCase()
  );
  const myId = me?.identifier || null;

  // Calendars mapped to a person's name (for calendar-based "theirs" matching)
  function calIdsForName(name) {
    return Object.entries(familyData?.config?.calendarAssignments || {})
      .filter(([, n]) => n?.toLowerCase() === name?.toLowerCase())
      .map(([id]) => id);
  }

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setAuthRequired(false);
    try {
      console.log('[FOH] fetchData start — familyData:', familyData?.id, 'config:', familyData?.config);
      // Get the date range — use the shared helper so range buttons actually work
      const { start: timeMin, end: timeMax } = getViewRangeDates(rangeKey);

      // Get family config from Firestore
      const appConfig = familyData?.config || {};
      const calAssignments = appConfig.calendarAssignments || {};
      const suppressedCals = appConfig.suppressedCalendars || [];
      const carePartners = familyData?.carePartners || [];

      // Determine which calendar IDs to fetch
      // Use assigned calendars; fall back to primary if none configured
      let calendarIds = Object.keys(calAssignments).filter(id => !suppressedCals.includes(id));
      if (calendarIds.length === 0) {
        // No calendars configured yet — fetch primary only
        calendarIds = ['primary'];
      }

      // Fetch events from GCal
      console.log('[FOH] fetching GCal — calendarIds:', calendarIds, 'timeMin:', timeMin.toISOString(), 'timeMax:', timeMax.toISOString());
      // Fetch per-calendar and merge
      const rawEvents = [];
      let calAuthRequired = false;
      for (const calId of calendarIds) {
        try {
          const evts = await gcalFetchEvents({
            calendarId: calId,
            timeMin: timeMin.toISOString(),
            timeMax: timeMax.toISOString(),
            maxResults: 250,
          });
          for (const e of (evts || [])) rawEvents.push({ ...e, _calendarId: calId });
        } catch (calErr) {
          console.warn('[FOH] calendar fetch failed:', calId, calErr.message);
          if (calErr instanceof GCalAuthError) calAuthRequired = true;
        }
      }
      if (calAuthRequired) setAuthRequired(true);
      // Deduplicate by event id
      const seenIds = new Set();
      const dedupedRaw = rawEvents.filter(e => { if (seenIds.has(e.id)) return false; seenIds.add(e.id); return true; });
      // Use dedupedRaw going forward
      const allFetchedEvents = dedupedRaw;
      console.log('[FOH] fetched events:', allFetchedEvents.length);

      // Load sitter sessions from Firestore for this date range
      const startDateStr = timeMin.toISOString().split('T')[0];
      const endDateStr   = timeMax.toISOString().split('T')[0];
      const sessionsByDate = {};
      // Keyed by session doc id — lets the event-building loop below cross-
      // reference a calendar event's fohMeta.session back to its session's
      // current status (declined or not), independent of which day it's on.
      const sessionsById = {};
      try {
        const sessions = await fsGetCol(`families/${familyData.id}/sessions`, [
          { field: 'date', op: '>=', value: startDateStr },
        ]);
        const today = new Date().toISOString().split('T')[0];
        const upcoming = [];
        for (const s of (sessions || [])) {
          if (s.date > endDateStr) continue;
          // A family-cancelled session (see handleLockIn's cancel branch)
          // should leave no trace — chip strip, day badges, etc.
          if (s.status === SESSION_STATUS.CANCELLED) continue;
          sessionsById[s.id] = s;
          if (!sessionsByDate[s.date]) sessionsByDate[s.date] = [];
          sessionsByDate[s.date].push(s);
          if (s.date >= today) upcoming.push(s);
        }
        upcoming.sort((a, b) => a.date.localeCompare(b.date));
        setUpcomingSessions(upcoming);
      } catch (err) {
        console.warn('[FOH] sessions fetch failed:', err.message);
      }

      // Load per-day pickup overrides for this date range
      const overridesMap = {};
      try {
        const overrides = await fsGetCol(`families/${familyData.id}/pickupOverrides`, [
          { field: 'date', op: '>=', value: startDateStr },
        ]);
        for (const o of (overrides || [])) {
          if (o.date > endDateStr) continue;
          overridesMap[overrideKey(o.childId, o.date, o.routineId, o.leg)] = o;
        }
      } catch (err) {
        console.warn('[FOH] pickup overrides fetch failed:', err.message);
      }

      // Load locally-hidden events (read-only-calendar noise) — never written back
      let ignoredMap = {};
      try {
        const ignored = await fsGetCol(`families/${familyData.id}/ignoredEvents`);
        for (const i of (ignored || [])) ignoredMap[i.id] = i;
      } catch (err) {
        console.warn('[FOH] ignored events fetch failed:', err.message);
      }

      // Group events by date, attach person name from calendar assignment.
      // Multi-day (usually all-day) events are placed in every day they
      // cover, not just their start day — clipped to the fetched window.
      const byDate = {};
      for (const evt of allFetchedEvents) {
        if (ignoredMap[evt.id] || (evt.recurringEventId && ignoredMap[evt.recurringEventId])) continue;
        const dates = eventDates(evt).filter(d => d >= startDateStr && d <= endDateStr);
        if (!dates.length) continue;
        const personName = calAssignments[evt._calendarId] || 'Other';
        const fohMeta = parseFOHMeta(evt.description);
        const linkedSession = fohMeta?.session ? sessionsById[fohMeta.session] : null;
        // Declined is checked from the sessions doc (source of truth), not
        // from GCal metadata — the caregiver's decline may not have been
        // reconciled into this event's description yet (see the
        // reconcile-on-read pass below), and shouldn't need to be for this
        // to show up correctly.
        const isDeclinedSession = linkedSession?.status === SESSION_STATUS.DECLINED;
        // `session` is the current marker for a caregiver-booked event;
        // `assignee === 'sitter'` is a permanent legacy fallback for events
        // written before ScheduleSitter used the standard FOH assignee field
        // (same pattern as the reminder/exempt legacy-key fallback above). A
        // declined session is never "covered" — falls back into the normal
        // needs-assignment UI on purpose, so it can't get lost.
        const isSitterBlock = (!!fohMeta?.session || fohMeta?.assignee === 'sitter') && !isDeclinedSession;
        const eventRow = {
          id: evt.id,
          summary: evt.summary || '(No title)',
          startTime: formatEventTime(evt),
          calendarColor: getCalendarColor(evt._calendarId, calendarIds),
          _calendarId: evt._calendarId,
          _personName: personName,
          // sitter block events are auto-reminder — they ARE the coverage, not things needing it
          fohMeta: isSitterBlock ? { ...fohMeta, reminder: true } : fohMeta,
          isSitterBlock,
          isDeclinedSession,
          readOnly: evt.organizer?.self === false,
          _raw: evt,
        };
        for (const date of dates) {
          if (!byDate[date]) byDate[date] = [];
          byDate[date].push(eventRow);
        }
      }

      // Build day objects across the range
      const days = [];
      const cursor = new Date(timeMin);
      while (cursor <= timeMax) {
        const isoDate = cursor.toISOString().split('T')[0];
        const dayEvents = byDate[isoDate] || [];

        // Run rules engine on this day
        // rules engine imported at top — see import below
        const alerts = evaluateDay({ isoDate, events: dayEvents }, carePartners, appConfig);

        // Derive person columns
        const children = familyData?.children?.map(c => c.firstName) || [];
        const familySitters = familyData?.sitters?.filter(s => s.sitterType === 'family' && s.active !== false) || [];

        days.push({
          isoDate,
          dayOfWeek: cursor.toLocaleDateString('en-US', { weekday: 'long' }),
          fullDate: cursor.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
          alertLevel: alerts.length === 0 ? 'clear' : alerts[0].level,
          alerts,
          contextSummary: buildContextSummary(alerts, dayEvents),
          hasSitterPending: (sessionsByDate[isoDate] || []).length > 0,
          sitterSessions: sessionsByDate[isoDate] || [],
          carePartners: carePartners.map(cp => ({
            identifier: cp.identifier,
            name: cp.name,
            hasConflict: alerts.some(a => a.carePartner?.id === cp.id),
          })),
          familySitters: familySitters.map(fs => ({
            identifier: fs.identifier || fs.firstName[0].toUpperCase(),
            name: fs.firstName,
            sitterType: 'family',
            pickupEligible: fs.pickupEligible || false,
          })),
          children,
          events: dayEvents,
          pickups: pickupsForDate(familyData?.children || [], isoDate, overridesMap, { includeSkipped: true, active: familyData?.config?.pickupsActive !== false }),
          carePartnerRecords: carePartners,
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      // Reclassify each day based on what the user actually cares about:
      //
      //  conflict   — a care partner has a work/unavailable event AND there are
      //               kid events or unresolved coverage on that day with no assignee.
      //               Shown in red. Something needs to be sorted.
      //
      //  unassigned — events exist (any type) with no FOH assignee and not a reminder.
      //               Shown in amber. Needs attention but no confirmed conflict yet.
      //
      //  assigned   — every event is either assigned, a reminder, or a care partner's
      //               own personal event. Shown with a check. Nothing to do.

      const reclassified = days.map(day => {
        const events = day.events || [];

        // Helper: is this event "resolved" (assigned, a reminder, or title-tagged)?
        // A declined session is never resolved, even before the FOH
        // metadata's own assignee field has been cleared by the
        // reconcile-on-read pass below — the sessions doc is the source of
        // truth here, not whatever the calendar description still says.
        function isResolved(e) {
          if (e.isDeclinedSession) return false;
          if (e.isSitterBlock) return true;
          if (e.fohMeta?.reminder || e.fohMeta?.assignee) return true;
          if (/\([A-Z][A-Z\-\/]{0,5}\)\s*$/.test(e.summary || '')) return true;
          return false;
        }

        // Care partner personal events (their own calendar) — not logistics events
        const cpCalIds = new Set(
          carePartners.map(cp => cp.calendarId).filter(Boolean)
        );

        // Logistics events = events NOT on a care partner's personal calendar
        // These are kid events, family events, school events etc. that need assignment
        const logisticsEvents = events.filter(e => !cpCalIds.has(e._calendarId));

        // Care partner work/unavailable events (on their own calendar)
        const cpWorkEvents = events.filter(e =>
          cpCalIds.has(e._calendarId) &&
          !isResolved(e) &&
          (e.startTime !== 'All day' || e.fohMeta === null) // any timed or flagged all-day
        );

        const unresolved = logisticsEvents.filter(e => !isResolved(e));
        const hasUnresolved = unresolved.length > 0;

        // Conflict: rules engine fired high alert (both unavailable, school day gap etc.)
        // OR: a care partner has work events AND logistics events are unresolved
        // OR: a caregiver declined a session that day — always forced red,
        // not just "unassigned", so it can't be mistaken for routine
        // unassigned-but-fine backlog.
        const hasConflict =
          events.some(e => e.isDeclinedSession) ||
          day.alerts?.some(a => a.level === 'high') ||
          (cpWorkEvents.length > 0 && hasUnresolved);

        if (hasConflict) return {
          ...day,
          alertLevel: 'conflict',
          unresolvedCount: unresolved.length,
        };

        // Unassigned: logistics events exist but none assigned yet, no confirmed conflict
        if (hasUnresolved) return {
          ...day,
          alertLevel: 'unassigned',
          unresolvedCount: unresolved.length,
        };

        return { ...day, alertLevel: 'assigned', unresolvedCount: 0 };
      });

      const conflict   = reclassified.filter(d => d.alertLevel === 'conflict').length;
      const unassigned = reclassified.filter(d => d.alertLevel === 'unassigned').length;
      const assigned   = reclassified.filter(d => d.alertLevel === 'assigned').length;

      console.log('[FOH] days:', reclassified.length, '— conflict:', conflict, 'unassigned:', unassigned, 'assigned:', assigned);

      // Reconcile-on-read: a caregiver's decline can only ever write to
      // Firestore (their browser has no Google Calendar access — never
      // authenticated with Google), so the calendar event's own title/FOH
      // metadata may still say "assigned" until a parent's browser happens
      // to load this data and patch it. Best-effort, non-blocking, and
      // self-limiting (skips events whose title already carries the
      // suffix) — the red highlight above never depends on this having run.
      for (const day of reclassified) {
        for (const evt of day.events) {
          if (!evt.isDeclinedSession || evt.summary?.includes('(Declined)')) continue;
          setEventAssignee(evt, null, { session: evt.fohMeta?.session, titleSuffix: ' (Declined)' }).catch(() => {});
        }
      }

      setDays(reclassified);
      setDataAsOf(new Date());
      return reclassified;
    } catch (err) {
      console.error('fetchData error:', err);
      setError(err.message);
      // Fall back to stub data so UI isn't empty
      const stubDays = generateStubDays(rangeKey);
      setDays(stubDays);
    } finally {
      setLoading(false);
    }
  };

  async function handleReconnect() {
    try {
      await reconnectGCal();
      fetchData();
    } catch (err) {
      console.error('[FOH] reconnect failed:', err);
    }
  }

  // Re-fetch whenever range or family changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [rangeKey, familyData?.id]);

  // Stale check
  useEffect(() => {
    const interval = setInterval(() => {
      if (dataAsOf) {
        const ageMin = (Date.now() - dataAsOf.getTime()) / 60000;
        setStale(ageMin > 30);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [dataAsOf]);

  function handleAssign(eventId, assigneeCode, fromState, scope = 'instance') {
    setPendingAssignments(prev => ({
      ...prev,
      [eventId]: { assignee: assigneeCode, previousAssignee: fromState?.assignee || null, scope, dirty: true },
    }));
  }

  // pickup: a resolved leg row (carries childId/date/routineId/leg/key).
  // adultId: 'A'|'K'|'cg:{sitterId}'|'skip', or null to revert to the weekly default.
  function handleAssignPickup(pickup, adultId) {
    setPendingAssignments(prev => ({
      ...prev,
      [`pickup_${pickup.key}`]: {
        kind: 'pickup', childId: pickup.childId, date: pickup.date,
        routineId: pickup.routineId, leg: pickup.leg, overrideKey: pickup.key,
        adultId, previousAdultId: pickup.adultId || null,
        revert: adultId === null, dirty: true,
      },
    }));
  }

  function handleCancelEvent(eventId, reason) {
    setPendingAssignments(prev => ({
      ...prev,
      [eventId]: { ...prev[eventId], cancelled: true, cancelReason: reason },
    }));
  }

  async function handleToggleReminder(eventId, scope = 'instance') {
    const event = days.flatMap(d => d.events).find(e => e.id === eventId);
    if (!event) return;
    if (event.readOnly) return; // can't patch events we don't own

    // Toggle: if already a reminder, clear it; otherwise mark as one
    const alreadyReminder = pendingAssignments[eventId]?.reminder ?? event.fohMeta?.reminder ?? false;
    const nextReminder = !alreadyReminder;

    // Optimistically update pending so UI reflects immediately
    setPendingAssignments(prev => ({
      ...prev,
      [eventId]: { ...prev[eventId], reminder: nextReminder, dirty: true },
    }));

    // Write directly to GCal — reminder doesn't need Lock In.
    // scope 'series' patches the recurring event's master, which propagates the
    // title/description to all non-exception instances; instances that already
    // have a per-instance exception keep their own values.
    const targetId = (scope === 'series' && event._raw?.recurringEventId)
      ? event._raw.recurringEventId : eventId;
    try {
      const newDesc = buildFOHDescription(event._raw?.description || '', {
        assignee: event.fohMeta?.assignee || pendingAssignments[eventId]?.assignee || null,
        reviewed: true,
        reminder: nextReminder,
      });

      // Reminder state lives only in the description block + UI bell — the
      // title is never touched.
      const baseTitle = stripAssigneeTag(event.summary || '');

      await gcalPatchEvent(event._calendarId, targetId, {
        summary: baseTitle,
        description: newDesc,
      });

      if (familyData?.id) {
        await fsLogChange(familyData.id, {
          actorId: currentUser?.email,
          actorName: currentUser?.name,
          module: 'logistics',
          action: nextReminder ? 'reminder_event' : 'unreminder_event',
          entityType: 'event',
          entityId: eventId,
          toValue: `${nextReminder ? 'reminder' : 'active'} — ${baseTitle}`,
        });
      }
    } catch (err) {
      console.error('handleToggleReminder failed:', err);
      // Roll back optimistic update on failure
      setPendingAssignments(prev => ({
        ...prev,
        [eventId]: { ...prev[eventId], reminder: alreadyReminder },
      }));
    }
  }

  // Hides an event locally (Firestore-only) without ever writing back to its
  // calendar — the only option for read-only calendars (e.g. school calendar
  // noise like Board of Ed meetings). scope 'series' hides every occurrence.
  async function handleIgnoreEvent(eventId, recurringEventId, scope = 'instance') {
    const event = days.flatMap(d => d.events).find(e => e.id === eventId);
    if (!familyData?.id || !event) return;
    const targetId = (scope === 'series' && recurringEventId) ? recurringEventId : eventId;

    try {
      await fsSet(`families/${familyData.id}/ignoredEvents/${targetId}`, {
        targetId, scope, calendarId: event._calendarId, title: event.summary,
        actorId: currentUser?.email, actorName: currentUser?.name,
        createdAt: new Date().toISOString(),
      });
      await fsLogChange(familyData.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'ignore_event',
        entityType: 'event', entityId: targetId,
        toValue: `${scope}: ${event.summary}`,
      });
      await fetchData();
    } catch (err) {
      console.error('handleIgnoreEvent failed:', err);
    }
  }

  async function handleLockIn() {
    const unreviewedItems = days.flatMap(d => d.events.filter(e => {
      const pending = pendingAssignments[e.id];
      return !pending?.assignee && !e.fohMeta?.reviewed && !e.fohMeta?.reminder;
    }));
    if (unreviewedItems.length > 0) {
      const confirmed = await showLockInWarning(unreviewedItems);
      if (!confirmed) return;
    }

    setLockingIn(true);
    const results = { patched: 0, failed: 0, errors: [] };

    for (const [key, pending] of Object.entries(pendingAssignments)) {
      // Pickup overrides write to Firestore, not GCal — handle first so the
      // event lookup below doesn't silently skip them (no matching event id).
      if (pending.kind === 'pickup') {
        try {
          const docId = pending.overrideKey || `${pending.childId}_${pending.date}`;
          const path = `families/${familyData.id}/pickupOverrides/${docId}`;
          if (pending.revert) await fsDelete(path);
          else await fsSet(path, {
            childId: pending.childId, date: pending.date,
            routineId: pending.routineId || null, leg: pending.leg || null,
            adultId: pending.adultId,
            actorId: currentUser?.email, actorName: currentUser?.name,
          });
          try {
            await fsLogChange(familyData.id, {
              actorId: currentUser?.email, actorName: currentUser?.name,
              module: 'logistics', action: 'override_pickup',
              entityType: 'pickup', entityId: docId,
              fromValue: pending.previousAdultId, toValue: pending.revert ? 'default' : pending.adultId,
            });
          } catch (logErr) {
            console.warn('[FOH] pickup changelog write failed:', logErr.message);
          }
          results.patched++;
        } catch (err) {
          console.error(`Lock In failed for pickup ${key}:`, err);
          results.failed++;
          results.errors.push({ eventId: key, summary: 'Pickup override', error: err.message });
        }
        continue;
      }

      // Find the event to get calendarId
      const eventId = key;
      const event = days.flatMap(d => d.events).find(e => e.id === eventId);
      if (!event) continue;
      if (event.readOnly) { results.patched++; continue; } // attendee-only event — skip GCal write, count as done

      try {
        if (pending.cancelled) {
          // Delete from GCal
          await gcalDeleteEvent(event._calendarId, eventId);
          // If this was a caregiver session's own block, mark the linked
          // session doc cancelled too — otherwise it'd keep showing up in
          // the chip strip, day badges, and the caregiver's own session list
          // even though the calendar event backing it is gone.
          if (event.fohMeta?.session) {
            try {
              await fsSet(`families/${familyData.id}/sessions/${event.fohMeta.session}`, {
                status: SESSION_STATUS.CANCELLED,
                cancelledAt: new Date().toISOString(),
                cancelledBy: currentUser?.email,
              });
              await fsLogChange(familyData.id, {
                actorId: currentUser?.email, actorName: currentUser?.name,
                module: 'logistics', action: 'cancel_sitter_session',
                entityType: 'session', entityId: event.fohMeta.session,
                toValue: `${pending.cancelReason || 'cancelled'} — ${event.summary}`,
              });
            } catch (sessErr) {
              console.warn('[FOH] session cancel write failed:', sessErr.message);
            }
          }
        } else if (pending.assignee) {
          // Build updated FOH description block
          const newDesc = buildFOHDescription(event._raw?.description || '', {
            assignee: pending.assignee,
            reviewed: true,
          }, currentUser?.given_name || 'A');

          // Append (ID) to title — strip any existing tag first, then add new one
          const baseTitle = stripAssigneeTag(event.summary || '');
          const newTitle = `${baseTitle} (${pending.assignee})`;

          // scope 'series' patches the recurring master, propagating the new
          // title/FOH block to all non-exception instances; a per-instance
          // exception on another day keeps its own title/description.
          const targetId = (pending.scope === 'series' && event._raw?.recurringEventId)
            ? event._raw.recurringEventId : eventId;

          await gcalPatchEvent(event._calendarId, targetId, {
            summary: newTitle,
            description: newDesc,
          });

          // Log to Firestore changelog
          if (familyData?.id) {
            await fsLogChange(familyData.id, {
              actorId: currentUser?.email,
              actorName: currentUser?.name,
              module: 'logistics',
              action: 'assign_event',
              entityType: 'event',
              entityId: eventId,
              fromValue: event.fohMeta?.assignee || null,
              toValue: `${pending.assignee}${pending.scope === 'series' ? ' (series)' : ''} — ${baseTitle}`,
            });
          }
        }
        results.patched++;
      } catch (err) {
        console.error(`Lock In failed for ${eventId}:`, err);
        results.failed++;
        results.errors.push({ eventId, summary: event.summary, error: err.message });
      }
    }

    setLockingIn(false);
    setLockInResult(results);
    setPendingAssignments({});

    // Refresh data to show updated FOH blocks. Caregiver publish is batched —
    // queue it rather than publishing this exact instant, so several quick
    // Lock Ins (or a Lock In plus other schedule-touching actions elsewhere)
    // in the same short window collapse into one write.
    if (results.patched > 0) {
      await fetchData();
      if (familyData?.id) scheduleBatchedPublish(familyData);
    }
  }

  // A day "needs action" when it has a conflict/unassigned event, or an unset pickup —
  // the alertLevel classification above is event-only, so pickups are checked separately.
  function dayNeedsAction(day) {
    if (day.alertLevel === 'conflict' || day.alertLevel === 'unassigned') return true;
    return (day.pickups || []).some(p => !p.adultId && !p.skipped);
  }
  const needsActionCount = days.filter(dayNeedsAction).length;

  const viewFilteredDays = viewFilter === 'needs_action'
    ? days.filter(dayNeedsAction)
    : days;

  // Effective person narrowing: the 'Mine' pill maps to the signed-in user;
  // the ⚙ tray dropdown narrows to any specific care partner. Days with
  // nothing for that person are hidden.
  const effectiveIdentifier = viewFilter === 'mine' ? myId : (personFilter !== 'all' ? personFilter : null);

  const filteredDays = (() => {
    if (!effectiveIdentifier) return viewFilteredDays;
    const person = (familyData?.carePartners || []).find(cp => cp.identifier === effectiveIdentifier);
    const calIds = person ? calIdsForName(person.name) : [];
    return viewFilteredDays
      .map(day => ({
        ...day,
        events:  (day.events  || []).filter(e => isAssignedTo(e, { identifier: effectiveIdentifier, calIds })),
        pickups: (day.pickups || []).filter(p => p.adultId === effectiveIdentifier),
      }))
      .filter(day => day.events.length > 0 || day.pickups.length > 0);
  })();

  const hasPendingChanges = Object.keys(pendingAssignments).length > 0;

  // Days collapsed out of view by the "needs action" default — shown as a
  // small summary line so it reads as "hidden because covered," not "gone."
  const hiddenByNeedsAction = viewFilter === 'needs_action' ? days.length - viewFilteredDays.length : 0;

  // Workload-balance tally — how the CURRENT date range's events are split
  // across people, independent of the needs-action/person filters above (it
  // should reflect total load, not just what's outstanding). Same effective-
  // assignee resolution used everywhere else in this component.
  const workloadTally = (() => {
    const counts = {};
    for (const day of days) {
      for (const e of (day.events || [])) {
        const pend = pendingAssignments[e.id];
        if (pend?.cancelled) continue;
        if (e.isDeclinedSession) continue;
        const code = pend?.assignee || e.fohMeta?.assignee;
        if (!code) continue;
        counts[code] = (counts[code] || 0) + 1;
      }
    }
    const rows = Object.entries(counts).map(([code, count]) => {
      const person = (familyData?.carePartners || []).find(c => c.identifier === code)
        || (familyData?.sitters || []).find(s => s.identifier === code);
      const name = person?.shortName || person?.name?.split(' ')[0] || code;
      return { code, name, count, color: PERSON_COLORS[name] || 'var(--forest)' };
    });
    rows.sort((a, b) => b.count - a.count);
    const max = rows.length ? rows[0].count : 0;
    return { rows, max };
  })();

  // Derive date range label from loaded days
  const dateRangeLabel = (() => {
    if (!days || days.length === 0) return '';
    const first = new Date(days[0].isoDate + 'T12:00:00');
    const last  = new Date(days[days.length - 1].isoDate + 'T12:00:00');
    const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
    return `${fmt(first)} – ${fmt(last)}`;
  })();

  if (showDefaults) {
    return <RecurringDefaults onBack={() => setShowDefaults(false)} />;
  }

  // "Add missing event" — same Caregiver/Work Travel/General/Medical/Vacation
  // choice as "Add to Schedule" from Home, not a bare general-event form. The
  // date carries through to the general/medical/vacation forms directly;
  // Caregiver/Work Travel don't take a prefilled date today (their forms are
  // due for a bigger redesign — see plan), so those just open blank.
  function closeAddEvent() { setAddEventDate(null); setAddEventType(null); }

  if (addEventDate && !addEventType) {
    return <ScheduleNav onSelect={setAddEventType} onBack={closeAddEvent} />;
  }
  if (addEventDate && addEventType === 'schedule_sitter') {
    return <ScheduleSitter onBack={() => setAddEventType(null)} />;
  }
  if (addEventDate && addEventType === 'schedule_travel') {
    return <ScheduleWorkTravel onBack={() => setAddEventType(null)} />;
  }
  if (addEventDate && addEventType?.startsWith('event_')) {
    const type = addEventType.replace('event_', '');
    return (
      <EventForm
        type={type}
        typeLabel={({ general: 'General', medical: 'Medical', vacation: 'Vacation' })[type] || 'Event'}
        initialDate={addEventDate}
        onBack={() => setAddEventType(null)}
        onClose={() => { closeAddEvent(); fetchData(); }}
      />
    );
  }

  return (
    <div className="review-screen">
      {/* Header */}
      <div className="review-header">
        <button className="btn btn--back" onClick={onBack}>← {backLabel}</button>
        <h1>Review & Assign</h1>
        <AccountMenu onSettings={onSettings} showName={false} />
      </div>

      {/* Stale warning — 🌧️ is reserved for real errors elsewhere in the app;
          this is just aging data, not a problem, so it gets its own leaf motif. */}
      {stale && (
        <div className="stale-banner">
          🍂 A few leaves have fallen since your last sync.{' '}
          <button onClick={fetchData}>Resync calendar</button>
        </div>
      )}

      {/* Data freshness — caregiver publish status/button now lives in
          Settings → Data & History → Reset & Data (Maintenance section),
          since batched auto-publish handles it in the background now. */}
      {dataAsOf && !stale && (
        <div className="freshness-note">
          Data as of {dataAsOf.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          <button className="btn btn--link" onClick={fetchData}>↻</button>
        </div>
      )}

      {/* Filter row — All · Needs action · Mine. Replaces the old alert-badge
          buttons and unstyled person chips with one triage-focused row. */}
      <div className="review-filter-row">
        <button
          className={`filter-pill ${viewFilter === 'all' ? 'filter-pill--active' : ''}`}
          onClick={() => setViewFilter('all')}
        >All</button>
        <button
          className={`filter-pill filter-pill--needs-action ${viewFilter === 'needs_action' ? 'filter-pill--active' : ''}`}
          onClick={() => setViewFilter(viewFilter === 'needs_action' ? 'all' : 'needs_action')}
          disabled={needsActionCount === 0}
        >
          {needsActionCount === 0 ? '✓ All assigned' : (
            <>Needs action <span className="filter-pill__count">{needsActionCount}</span></>
          )}
        </button>
        {myId && (
          <button
            className={`filter-pill ${viewFilter === 'mine' ? 'filter-pill--active' : ''}`}
            onClick={() => setViewFilter(viewFilter === 'mine' ? 'all' : 'mine')}
          >Mine</button>
        )}

        <div className="review-filter-row__actions">
          <button
            className="filter-pill filter-pill--recurring"
            onClick={() => setShowDefaults(true)}
            title="Manage recurring events"
          ><RecurringIcon /> Recurring</button>
          <button
            className={`filter-pill ${showViewOptions ? 'filter-pill--active' : ''}`}
            onClick={() => setShowViewOptions(!showViewOptions)}
            title="Change date range and filters"
          >
            {dateRangeLabel || 'Filters'}
            <span className={`review-range-btn__chevron ${showViewOptions ? 'review-range-btn__chevron--open' : ''}`} aria-hidden="true">⌄</span>
          </button>
        </div>
      </div>

      {hiddenByNeedsAction > 0 && (
        <p className="review-hidden-note">
          {hiddenByNeedsAction} day{hiddenByNeedsAction !== 1 ? 's' : ''} already covered — hidden
        </p>
      )}

      {/* View options tray */}
      {showViewOptions && (
        <div className="view-options-tray">
          <div className="view-options-tray__section">
            <label>Time Range</label>
            <div className="range-buttons">
              {VIEW_RANGES.map(r => (
                <button
                  key={r.value}
                  className={`range-btn ${rangeKey === r.value ? 'active' : ''}`}
                  onClick={() => { setRangeKey(r.value); setShowViewOptions(false); }}
                >{r.label}</button>
              ))}
            </div>
          </div>
          <div className="view-options-tray__section">
            <label>Person</label>
            <select
              className="view-options-tray__select"
              value={personFilter}
              onChange={e => setPersonFilter(e.target.value)}
            >
              <option value="all">Everyone</option>
              {(familyData?.carePartners || []).filter(cp => cp.identifier).map(cp => (
                <option key={cp.identifier} value={cp.identifier}>{cp.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="error-banner">
          🌧️ {error} — showing stub data
          <button className="btn btn--link" onClick={fetchData}>Retry</button>
        </div>
      )}
      {authRequired && <GCalReconnectPrompt onReconnect={handleReconnect} />}

      {/* Lock In result */}
      {lockInResult && (
        <div className={`lockin-result ${lockInResult.failed > 0 ? 'lockin-result--warn' : 'lockin-result--ok'}`}>
          {lockInResult.failed === 0
            ? `✓ Locked in ${lockInResult.patched} event${lockInResult.patched !== 1 ? 's' : ''} — calendar updated, caregivers will see it shortly`
            : `⚠ ${lockInResult.patched} updated, ${lockInResult.failed} failed — check console`}
          <button className="btn btn--link" onClick={() => setLockInResult(null)}>✕</button>
        </div>
      )}

      {/* Upcoming sitter sessions strip — suppressed for now, see SHOW_CAREGIVER_SESSION_HINTS */}
      {SHOW_CAREGIVER_SESSION_HINTS && upcomingSessions.length > 0 && !loading && (
        <div className="sessions-strip">
          <span className="sessions-strip__label">🌱 Caregiver sessions</span>
          <div className="sessions-strip__chips">
            {upcomingSessions.slice(0, 8).map((sess, i) => {
              const isDeclined = sess.status === SESSION_STATUS.DECLINED;
              return (
                <div key={i} className={`session-chip${isDeclined ? ' session-chip--declined' : ''}`}>
                  <span className="session-chip__name">{sess.sitterName?.split(' ')[0] || 'Caregiver'}</span>
                  <span className="session-chip__sep">·</span>
                  <span className="session-chip__date">{formatShortDate(sess.date)}</span>
                  <span className="session-chip__sep">·</span>
                  {isDeclined
                    ? <span className="session-chip__declined">Declined</span>
                    : <span className="session-chip__time">{sess.startTime}–{sess.endTime}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day stack + workload rail — rail is desktop-only (see .review-main-rail) */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          Loading calendar data…
        </div>
      ) : (
        <div className="review-main-rail">
          <div className="day-stack">
            {filteredDays.length === 0 && (
              <div className="empty-state" style={{ padding: 24 }}>
                {viewFilter !== 'all' || personFilter !== 'all' ? 'No days match this filter.' : 'No events found in this range.'}
              </div>
            )}
            {filteredDays.map(day => (
              <DayCard
                key={day.isoDate}
                day={day}
                pendingAssignments={pendingAssignments}
                onAssign={handleAssign}
                onAssignPickup={handleAssignPickup}
                onCancel={handleCancelEvent}
                onToggleReminder={handleToggleReminder}
                onIgnore={handleIgnoreEvent}
                onAddEvent={setAddEventDate}
                isExpanded={expandedDay === day.isoDate}
                onToggle={() => handleToggleDay(day.isoDate)}
                allSitters={familyData?.sitters || []}
              />
            ))}
          </div>
          <WorkloadRail tally={workloadTally} />
        </div>
      )}

      {/* Lock In footer */}
      {hasPendingChanges && (
        <div className="lock-in-footer">
          <span className="lock-in-footer__note">
            {Object.keys(pendingAssignments).length} unsaved action{Object.keys(pendingAssignments).length !== 1 ? 's' : ''} — not submitted yet
          </span>
          <button className="btn btn--lock-in" onClick={handleLockIn} disabled={lockingIn}>
            {lockingIn ? '⏳ Updating calendar…' : '🔒 Lock In'}
          </button>
        </div>
      )}
    </div>
  );
}

// Desktop-only workload-balance summary — how the current range's events
// split across people, so a wide window shows something a phone can't:
// who's carrying more this week, at a glance.
function WorkloadRail({ tally }) {
  if (!tally.rows.length) return null;
  return (
    <div className="workload-rail">
      <div className="workload-rail__title">Workload this range</div>
      {tally.rows.map(r => (
        <div className="workload-row" key={r.code}>
          <span className="workload-row__label" style={{ color: r.color }}>{r.name}</span>
          <div className="workload-row__track">
            <div
              className="workload-row__fill"
              style={{ width: `${tally.max ? (r.count / tally.max) * 100 : 0}%`, background: r.color }}
            />
          </div>
          <span className="workload-row__count">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

// ScheduleSitter now lives in its own file (src/logistics/ScheduleSitter.jsx)
// — imported at the top of this file.

// ─── EVENT FORM ───────────────────────────────────────────────────────────────

function EventForm({ type, typeLabel, onBack, onClose, initialDate = '' }) {
  const { familyData, currentUser } = useApp() || {};
  const isMedical = type === 'medical';
  const [form, setForm] = useState({
    title: '', date: initialDate, endDate: '', startTime: '', endTime: '',
    allDay: !isMedical, personId: '', calendarId: '', notes: '',
    assigneeId: '', // non-medical: who to assign the event to (optional)
    // Medical-only fields
    forPersonId: '', forPersonType: '', forPersonName: '',
    appointmentType: '', specialty: '', takerId: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState(null);

  const carePartners = familyData?.carePartners || [];
  const familySitters = (familyData?.sitters || []).filter(s => s.sitterType === 'family' && s.active !== false);
  const children     = familyData?.children || [];
  const calMeta      = familyData?.config?.calendarMeta || [];

  // Default calendar — first mapped calendar or primary
  const defaultCal = calMeta[0]?.id || 'primary';

  // For medical: derive the effective title from the structured fields.
  // "First-Name | Type" (e.g. "Katy | Dentist"), or "First-Name |
  // Specialist (specialty or doctor name)" for the Specialist button —
  // same "Name | ..." convention scripts/normalize-calendar-titles.js
  // applies retroactively (rules 3/11/12), kept in sync by hand.
  const medicalTitle = isMedical ? (() => {
    if (!form.forPersonName || !form.appointmentType) return '';
    if (form.appointmentType === 'Specialist') {
      const specialty = form.specialty?.trim();
      return specialty ? `${form.forPersonName} | Specialist (${specialty})` : '';
    }
    return `${form.forPersonName} | ${form.appointmentType}`;
  })() : '';

  const effectiveTitle = isMedical ? medicalTitle : form.title;

  async function handleSave() {
    if (isMedical) {
      if (!form.forPersonId)     { setError('Select who the appointment is for.'); return; }
      if (!form.appointmentType) { setError('Select appointment type.'); return; }
      if (form.appointmentType === 'Specialist' && !form.specialty.trim()) {
        setError('Enter the specialty or doctor name.'); return;
      }
      if (form.forPersonType === 'kid' && !form.takerId) {
        setError('Select which parent is taking them.'); return;
      }
      if (!form.date) { setError('Date is required.'); return; }
    } else {
      if (!form.title.trim() || !form.date) {
        setError('Title and date are required.'); return;
      }
    }
    setSaving(true); setError(null);
    const calId = form.calendarId || defaultCal;
    const tz    = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Compute assignee — medical: taker if kid, the person themselves if care partner;
    // general/other: whoever was picked in the optional "Assign to" chips;
    // vacation: no one — the whole family goes together, so it's a Reminder instead.
    const isVacation = type === 'vacation';
    const assignee = isMedical
      ? (form.forPersonType === 'kid' ? form.takerId : form.forPersonId)
      : (isVacation ? null : (form.assigneeId || null));

    const metaLines = [
      '-- Managed by Grove --',
      `type: ${type}`,
      assignee && `assignee: ${assignee}`,
      isVacation && 'reminder: yes',
      isMedical && `for: ${form.forPersonName}`,
      isMedical && `appointmentType: ${form.appointmentType}`,
      isMedical && form.specialty && `specialty: ${form.specialty.trim()}`,
    ].filter(Boolean).join('\n');

    try {
      const rawTitle = (effectiveTitle || '').trim();
      // Append (ID) to the title when assigned — consistent with Lock In's tagging
      const finalTitle = (!isMedical && assignee) ? `${rawTitle} (${assignee})` : rawTitle;
      const event = {
        summary:     finalTitle,
        description: form.notes ? `${form.notes}\n\n${metaLines}` : metaLines,
      };
      if (form.allDay) {
        event.start = { date: form.date };
        event.end   = { date: form.endDate || form.date };
      } else {
        event.start = { dateTime: `${form.date}T${form.startTime || '09:00'}:00`, timeZone: tz };
        event.end   = { dateTime: `${form.date}T${form.endTime   || '10:00'}:00`, timeZone: tz };
      }

      await createEvent(calId, event);

      await fsLogChange(familyData?.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'create_event', entityType: 'event',
        toValue: `${finalTitle} on ${form.date} (${type})`,
      });

      setSaved(true);
    } catch (err) {
      setError('Could not save: ' + err.message);
    }
    setSaving(false);
  }

  if (saved) return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onClose}>← Schedule</button>
        <h1>Schedule Event</h1>
      </div>
      <div className="travel-form" style={{ alignItems: 'center', paddingTop: 48, gap: 16 }}>
        <div style={{ fontSize: 40 }}>🍃</div>
        <h2 style={{ color: 'var(--forest-dark)' }}>Event added</h2>
        <p style={{ color: 'var(--sage)', textAlign: 'center' }}>Calendar updated.</p>
        <button className="btn btn--primary" onClick={onClose}>Done</button>
      </div>
    </div>
  );

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Schedule</button>
        <h1>{typeLabel} Event</h1>
      </div>
      <div className="travel-form">
        {error && <div className="warning-banner">{error}</div>}

        {isMedical && (
          <>
            {/* ── Section: Who is it for? ── */}
            <div className="form-section">
              <div className="form-section__title">Who is it for?</div>
              <div className="traveler-pills">
                {carePartners.map(cp => (
                  <button key={`cp-${cp.id}`} type="button"
                    className={`traveler-pill ${form.forPersonId === (cp.identifier || cp.id) ? 'active' : ''}`}
                    onClick={() => setForm(f => ({
                      ...f,
                      forPersonId: cp.identifier || cp.id,
                      forPersonType: 'carePartner',
                      forPersonName: cp.shortName || cp.name?.split(' ')[0] || cp.name,
                      takerId: '',
                    }))}>
                    {cp.name?.split(' ')[0] || cp.name}
                  </button>
                ))}
                {children.map(ch => (
                  <button key={`ch-${ch.id}`} type="button"
                    className={`traveler-pill ${form.forPersonId === ch.id ? 'active' : ''}`}
                    onClick={() => setForm(f => ({
                      ...f,
                      forPersonId: ch.id,
                      forPersonType: 'kid',
                      forPersonName: ch.firstName || ch.name?.split(' ')[0] || ch.name || 'Kid',
                    }))}>
                    {ch.firstName || ch.name}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Section: Appointment type ── */}
            <div className="form-section">
              <div className="form-section__title">Appointment Type</div>
              <div className="transport-options">
                {[['Doctor','🩺'], ['Dentist','🦷'], ['Specialist','🔬']].map(([t, icon]) => (
                  <button key={t} type="button"
                    className={`transport-btn ${form.appointmentType === t ? 'active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, appointmentType: t, specialty: t === 'Specialist' ? f.specialty : '' }))}>
                    {icon} {t}
                  </button>
                ))}
              </div>
              {form.appointmentType === 'Specialist' && (
                <>
                  <label className="form-label">Specialty or doctor name</label>
                  <input className="input" placeholder="e.g. Orthodontist, Dr. Smith"
                    value={form.specialty}
                    onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))} />
                </>
              )}
              {effectiveTitle && (
                <p className="settings-note" style={{ marginTop: 10 }}>
                  Event title: <strong>{effectiveTitle}</strong>
                </p>
              )}
            </div>

            {form.forPersonType === 'kid' && (
              <div className="form-section">
                <div className="form-section__title">Who is taking them?</div>
                <div className="traveler-pills">
                  {carePartners.map(cp => (
                    <button key={cp.id} type="button"
                      className={`traveler-pill ${form.takerId === (cp.identifier || cp.id) ? 'active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, takerId: cp.identifier || cp.id }))}>
                      {cp.name?.split(' ')[0] || cp.name}
                    </button>
                  ))}
                </div>
                <p className="settings-note" style={{ marginTop: 8 }}>
                  This will pre-assign the event so it appears as covered. You can change it later in Review & Assign.
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Event Details (title/all-day only for non-medical) ── */}
        <div className="form-section">
          <div className="form-section__title">{isMedical ? 'When' : 'Event Details'}</div>

          {!isMedical && (
            <>
              <label className="form-label">Title</label>
              <input className="input" placeholder={`e.g. ${type === 'vacation' ? 'Beach trip' : 'Family event'}`}
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />

              <label className="form-label">All day?</label>
              <div className="yes-no-toggle">
                <button className={`yn-btn ${form.allDay ? 'active' : ''}`}  onClick={() => setForm(f => ({ ...f, allDay: true }))}>Yes</button>
                <button className={`yn-btn ${!form.allDay ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, allDay: false }))}>No — timed</button>
              </div>

              {type === 'vacation' ? (
                <p className="settings-note"><ReminderIcon /> Saved as a Reminder — the whole family's going, so no one needs to be assigned.</p>
              ) : (
                <>
                  <label className="form-label">Assign to (optional)</label>
                  <div className="traveler-pills">
                    {carePartners.map(cp => (
                      <button key={cp.id} type="button"
                        className={`traveler-pill ${form.assigneeId === (cp.identifier || cp.id) ? 'active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, assigneeId: f.assigneeId === (cp.identifier || cp.id) ? '' : (cp.identifier || cp.id) }))}>
                        {cp.name?.split(' ')[0] || cp.name}
                      </button>
                    ))}
                    {familySitters.map(fs => (
                      <button key={fs.id} type="button"
                        className={`traveler-pill ${form.assigneeId === (fs.identifier || fs.id) ? 'active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, assigneeId: f.assigneeId === (fs.identifier || fs.id) ? '' : (fs.identifier || fs.id) }))}>
                        {fs.firstName}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {isMedical && (
            <>
              <label className="form-label">All day?</label>
              <div className="yes-no-toggle">
                <button className={`yn-btn ${form.allDay ? 'active' : ''}`}  onClick={() => setForm(f => ({ ...f, allDay: true }))}>Yes</button>
                <button className={`yn-btn ${!form.allDay ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, allDay: false }))}>No — timed</button>
              </div>
            </>
          )}

          <label className="form-label">{form.allDay && type === 'vacation' ? 'Start date' : 'Date'}</label>
          <input type="date" className="input" value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />

          {form.allDay && type === 'vacation' && (
            <>
              <label className="form-label">End date</label>
              <input type="date" className="input" value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </>
          )}

          {!form.allDay && (
            <div className="form-row">
              <div>
                <label className="form-label">Start time</label>
                <input type="time" className="input" value={form.startTime}
                  onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">End time</label>
                <input type="time" className="input" value={form.endTime}
                  onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} />
              </div>
            </div>
          )}
        </div>

        {/* ── Calendar & Notes ── */}
        <div className="form-section">
          <div className="form-section__title">Calendar & Notes</div>

          <label className="form-label">Calendar</label>
          <select className="input" value={form.calendarId || defaultCal}
            onChange={e => setForm(f => ({ ...f, calendarId: e.target.value }))}>
            {calMeta.length > 0
              ? calMeta.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)
              : <option value="primary">Primary calendar</option>
            }
          </select>

          <label className="form-label">Notes (optional)</label>
          <textarea className="input input--textarea" rows={2} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        <button className="btn btn--primary btn--full"
          disabled={saving || !effectiveTitle.trim() || !form.date}
          onClick={handleSave}>
          {saving ? 'Adding…' : isMedical ? '🩺 Add Appointment' : '🍃 Add to Calendar'}
        </button>
      </div>
    </div>
  );
}

// ─── SCHEDULE WORK TRAVEL ─────────────────────────────────────────────────────

function ScheduleWorkTravel({ onBack }) {
  const { familyData, currentUser } = useApp() || {};
  const [form, setForm] = useState({
    travelerId: '', startDate: '', endDate: '',
    canDoDropoff: null, homeForLastDay: null,
    location: '', transport: '', description: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState(null);

  const carePartners = familyData?.carePartners || [];

  function getTravelerCalId() {
    const cp = carePartners.find(c => c.identifier === form.travelerId || c.id === form.travelerId);
    if (!cp) return 'primary';
    const calAssign = familyData?.config?.calendarAssignments || {};
    const entry = Object.entries(calAssign).find(([, name]) => name?.toLowerCase() === cp.name?.toLowerCase());
    return entry?.[0] || 'primary';
  }

  async function handleSave() {
    if (!form.travelerId || !form.startDate || !form.endDate) {
      setError('Traveler, departure date, and return date are required.'); return;
    }
    setSaving(true); setError(null);
    const cp  = carePartners.find(c => c.identifier === form.travelerId || c.id === form.travelerId);
    const calId = getTravelerCalId();
    const travelerName = cp?.name || form.travelerId;
    const firstName = travelerName.trim().split(/\s+/)[0];
    const employer = cp?.employer?.trim();
    const destination = form.location || 'Work trip';
    // "First-Name | Employer-Work Travel | Location" — same convention
    // scripts/normalize-calendar-titles.js applies retroactively (rule 10),
    // kept in sync by hand so new travel events don't need re-normalizing.
    const label = employer ? `${employer}-Work Travel` : 'Work Travel';
    const summary = `${firstName} | ${label} | ${destination}`;

    const notes = [
      form.location    && `Location: ${form.location}`,
      form.transport   && `Transport: ${form.transport}`,
      form.canDoDropoff  !== null && `Can do drop-off day 1: ${form.canDoDropoff ? 'Yes' : 'No'}`,
      form.homeForLastDay !== null && `Home for last day: ${form.homeForLastDay ? 'Yes' : 'No'}`,
      form.description && form.description,
    ].filter(Boolean).join('\n');

    const description = `${notes}\n\n-- Managed by Grove --\ntype: travel | traveler: ${travelerName}`;

    try {
      // Invite other care partners as attendees on the ONE travel event —
      // Google Calendar puts invited events on their calendar and sends its
      // own invite notification, so no duplicate "heads-up" event is needed.
      const otherPartners = carePartners.filter(c =>
        c.identifier !== form.travelerId && c.id !== form.travelerId
      );
      const attendees = otherPartners.filter(o => o.email).map(o => ({ email: o.email }));

      await createEvent(calId, {
        summary,
        description,
        start: { date: form.startDate },
        end:   { date: form.endDate   },
        ...(attendees.length > 0 ? { attendees } : {}),
        reminders: { useDefault: false, overrides: [{ method: 'popup', minutes: 60 }] },
      }, { sendUpdates: attendees.length > 0 ? 'all' : undefined });

      await fsLogChange(familyData?.id, {
        actorId: currentUser?.email, actorName: currentUser?.name,
        module: 'logistics', action: 'schedule_travel', entityType: 'event',
        toValue: `${travelerName} travel ${form.startDate}–${form.endDate}`,
      });

      setSaved(true);
    } catch (err) {
      setError('Could not save: ' + err.message);
    }
    setSaving(false);
  }

  if (saved) return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Schedule</button>
        <h1>Schedule Work Travel</h1>
      </div>
      <div className="form-body" style={{ alignItems: 'center', paddingTop: 48, gap: 16 }}>
        <div style={{ fontSize: 40 }}>🌬️</div>
        <h2 style={{ color: 'var(--forest-dark)' }}>Travel added</h2>
        <p style={{ color: 'var(--sage)', textAlign: 'center' }}>
          Calendar event created — other care partners invited so it shows on their calendar too.
        </p>
        <button className="btn btn--primary" onClick={onBack}>Done</button>
      </div>
    </div>
  );

  return (
    <div className="module-screen">
      <div className="module-header">
        <button className="btn btn--back" onClick={onBack}>← Schedule</button>
        <h1>Schedule Work Travel</h1>
      </div>
      <div className="travel-form">
        {error && <div className="warning-banner">{error}</div>}

        {/* ── Section 1: Who & When ── */}
        <div className="form-section">
          <div className="form-section__title">Traveler & Dates</div>

          <label className="form-label">Who is traveling?</label>
          <div className="traveler-pills">
            {carePartners.map(cp => (
              <button
                key={cp.id}
                className={`traveler-pill ${form.travelerId === (cp.identifier || cp.id) ? 'active' : ''}`}
                onClick={() => setForm(f => ({ ...f, travelerId: cp.identifier || cp.id }))}>
                {cp.name?.split(' ')[0] || cp.name}
              </button>
            ))}
          </div>

          <div className="form-row" style={{ marginTop: 14 }}>
            <div>
              <label className="form-label">Departure date</label>
              <input type="date" className="input" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Return date</label>
              <input type="date" className="input" value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* ── Section 2: Coverage Questions ── */}
        <div className="form-section">
          <div className="form-section__title">Coverage Windows</div>
          <div className="coverage-q">
            <div className="coverage-q__item">
              <span className="coverage-q__label">Can you do drop-off on the first day?</span>
              <div className="yes-no-toggle">
                <button className={`yn-btn ${form.canDoDropoff === true  ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, canDoDropoff: true }))}>Yes</button>
                <button className={`yn-btn ${form.canDoDropoff === false ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, canDoDropoff: false }))}>No</button>
              </div>
            </div>
            <div className="coverage-q__item">
              <span className="coverage-q__label">Home in time to help on the last day?</span>
              <div className="yes-no-toggle">
                <button className={`yn-btn ${form.homeForLastDay === true  ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, homeForLastDay: true }))}>Yes</button>
                <button className={`yn-btn ${form.homeForLastDay === false ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, homeForLastDay: false }))}>No</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 3: Trip Details ── */}
        <div className="form-section">
          <div className="form-section__title">Trip Details</div>

          <label className="form-label">Destination</label>
          <input className="input" placeholder="e.g. Plano, TX  or  NYC HQ"
            value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />

          <label className="form-label">How are you getting there?</label>
          <div className="transport-options">
            {[['Driving','🚗'],['Train','🚆'],['Flying','✈️']].map(([t, icon]) => (
              <button key={t} className={`transport-btn ${form.transport === t ? 'active' : ''}`}
                onClick={() => setForm(f => ({ ...f, transport: t }))}>
                {icon} {t}
              </button>
            ))}
          </div>

          <label className="form-label">Notes (optional)</label>
          <textarea className="input input--textarea" rows={2} value={form.description}
            placeholder="e.g. Flight lands at 8pm, should be home by 9:30"
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>

        <button className="btn btn--primary btn--full"
          disabled={saving || !form.travelerId || !form.startDate || !form.endDate}
          onClick={handleSave}
          style={{ marginTop: 4 }}>
          {saving ? 'Adding…' : '🌬️ Add to Calendar & Invite Partner'}
        </button>
      </div>
    </div>
  );
}


// ─── WIRING HELPERS ──────────────────────────────────────────────────────────

function formatShortDate(iso) {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
}

function getCalendarColor(calendarId, allCalendarIds) {
  // Deterministic color from calendar ID index
  const COLORS = ['#4285F4','#E67C73','#33B679','#F6BF26','#7986CB','#039BE5','#0B8043'];
  const idx = allCalendarIds.indexOf(calendarId);
  return COLORS[idx % COLORS.length];
}

function buildContextSummary(alerts, events) {
  if (alerts.length === 0) return null;
  const msgs = alerts.filter(a => a.level !== 'info').map(a => a.message);
  return msgs.join('. ') + (msgs.length > 0 ? '.' : '');
}

// ─── STUB DATA FOR DEV ────────────────────────────────────────────────────────

function generateStubDays(rangeKey) {
  const today = new Date();
  const days = [];

  const carePartners = [
    { identifier: 'A', name: 'Adreck', hasConflict: false },
    { identifier: 'K', name: 'Katy',   hasConflict: false },
  ];
  // Family sitters — appear in assignee strip just like care partners
  const familySitters = [
    { identifier: 'G', name: 'Grampy', sitterType: 'family', pickupEligible: true },
    { identifier: 'D', name: 'Deedle', sitterType: 'family', pickupEligible: true },
  ];
  const children = ['Cal', 'Anylah', 'Lee', 'Sam'];

  const SAMPLE_DAYS = [
    {
      offset: 0,
      alertLevel: 'clear',
      alerts: [],
      contextSummary: null,
      events: [
        { id: 'e0-1', summary: 'Clinical',         startTime: 'All day',          calendarColor: '#E67C73', _personName: 'Katy',   _calendarId: 'katy' },
        { id: 'e0-2', summary: 'Pre-Team Swim',    startTime: '10:15–11:00 AM',   calendarColor: '#33B679', _personName: 'Cal',    _calendarId: 'cal' },
        { id: 'e0-3', summary: 'Silver Swim',      startTime: '8:00–9:30 AM',     calendarColor: '#0B8043', _personName: 'Sam',    _calendarId: 'sam' },
      ],
    },
    {
      offset: 1,
      alertLevel: 'high',
      alerts: [{ level: 'high', message: 'Both busy' }],
      contextSummary: 'Adreck has office all day. Katy has clinical 7–2 PM. Cal & Anylah have swim 10:15 AM. Confirm drop-off coverage.',
      events: [
        { id: 'e1-1', summary: 'Office',             startTime: 'All day',          calendarColor: '#3A5EA8', _personName: 'Adreck', _calendarId: 'adreck' },
        { id: 'e1-2', summary: 'Back-Up Care™',      startTime: 'All day',          calendarColor: '#3A5EA8', _personName: 'Adreck', _calendarId: 'adreck' },
        { id: 'e1-3', summary: 'Clinical',            startTime: '7:00–2:00 PM',     calendarColor: '#E67C73', _personName: 'Katy',   _calendarId: 'katy' },
        { id: 'e1-4', summary: 'Pre-Team Swim',       startTime: '10:15–11:00 AM',   calendarColor: '#33B679', _personName: 'Cal',    _calendarId: 'cal' },
        { id: 'e1-5', summary: 'Silver Swim',         startTime: '8:00–9:30 AM',     calendarColor: '#0B8043', _personName: 'Sam',    _calendarId: 'sam' },
      ],
    },
    {
      offset: 2,
      alertLevel: 'clear',
      alerts: [],
      contextSummary: null,
      events: [
        { id: 'e2-1', summary: 'Work From Home',    startTime: 'All day',           calendarColor: '#3A5EA8', _personName: 'Adreck', _calendarId: 'adreck' },
        { id: 'e2-2', summary: 'Tutoring',          startTime: '4:00–5:00 PM',      calendarColor: '#F6BF26', _personName: 'Anylah', _calendarId: 'anylah' },
      ],
    },
    {
      offset: 3,
      alertLevel: 'medium',
      alerts: [{ level: 'medium', message: 'Work day overlap' }],
      contextSummary: 'Katy has Peloton at 4:40 PM. Lee has soccer at 5 PM — driver needed.',
      events: [
        { id: 'e3-1', summary: 'Office',                  startTime: 'All day',      calendarColor: '#3A5EA8', _personName: 'Adreck', _calendarId: 'adreck' },
        { id: 'e3-2', summary: '60 min Power Zone Ride',  startTime: '4:40–5:40 PM', calendarColor: '#E67C73', _personName: 'Katy',   _calendarId: 'katy' },
        { id: 'e3-3', summary: 'Soccer Practice',         startTime: '5:00–6:30 PM', calendarColor: '#33B679', _personName: 'Lee',    _calendarId: 'lee' },
      ],
    },
  ];

  let dayCount = 0;
  for (let i = 0; dayCount < 10; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const sample = SAMPLE_DAYS[dayCount % SAMPLE_DAYS.length];
    const bothConflict = sample.alertLevel === 'high';

    days.push({
      isoDate: d.toISOString().split('T')[0],
      dayOfWeek: d.toLocaleDateString('en-US', { weekday: 'long' }),
      fullDate: d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
      alertLevel: sample.alertLevel,
      alerts: sample.alerts,
      contextSummary: sample.contextSummary,
      hasSitterPending: dayCount === 4,
      carePartners: carePartners.map(cp => ({ ...cp, hasConflict: bothConflict })),
      familySitters,
      children,
      events: sample.events.map(e => ({ ...e, fohMeta: null })),
    });
    dayCount++;
  }
  return days;
}

async function showLockInWarning(unreviewedItems) {
  // TODO: replace with proper modal
  return window.confirm(
    `${unreviewedItems.length} event(s) have no assignment or review:\n` +
    unreviewedItems.slice(0, 5).map(e => `• ${e.summary}`).join('\n') +
    '\n\nLock In anyway?'
  );
}
